import { DEFAULT_OFFSET, OFFSET_MAX, OFFSET_MIN } from '../constants/wrap-offsets'
import { parseOffsetCandidate } from '../helpers/offset-input'
import {
  buildAllOffsetsEntries,
  buildSideOffsetEntries,
  commonOffsetAfterSideEdit,
} from '../helpers/wrap-commit-entries'
import type { CommitEntry, OffsetKey, WrapTarget } from '../types/wrap-offset'
import type { useCommonOffsetInput } from './use-common-offset-input'
import type { useSideOffsetInputs } from './use-side-offset-inputs'

type UseOffsetCommitArgs = {
  targets: WrapTarget[]
  sideInputs: ReturnType<typeof useSideOffsetInputs>
  commonInput: ReturnType<typeof useCommonOffsetInput>
  commitToTargets: (entries: CommitEntry[]) => void
}

/** Commit paths for the directional inputs and the shared preset/slider/input control. */
export function useOffsetCommit({
  targets,
  sideInputs,
  commonInput,
  commitToTargets,
}: UseOffsetCommitArgs) {
  /** Invoked from `onBlur` only once the field is already known to hold a valid, in-range number
   *  (`offsetErrorMessages[key]` is '') — an invalid value reverts instead, see the input's
   *  `onBlur` (`SideOffsetInput`). Decimals are preserved as-is (no rounding). Applies to every
   *  selected target for which this side is real for its own current style (AC 4/7 — "do not
   *  accidentally overwrite disabled directional offsets"); a target whose style doesn't use this
   *  side is skipped entirely, not patched with an inapplicable value. */
  const commitOffset = (key: OffsetKey, raw: string) => {
    if (targets.length === 0) return
    const next = parseOffsetCandidate(raw) ?? DEFAULT_OFFSET

    sideInputs.markCommitted(key, next)

    const entries = buildSideOffsetEntries(targets, key, next)

    // Keep the shared preset/slider/input control in sync with the just-edited side — showing a
    // "Mixed" state instead of a stale/misleading single value when the selection now disagrees.
    if (!commonInput.commonInputFocused.current) {
      commonInput.showAggregate(commonOffsetAfterSideEdit(targets, key, next))
    }

    commitToTargets(entries)
  }

  /** Bounding Box: sets all four sides to the same value in a single commit (one undo entry) —
   *  the shared preset/slider/input control's "apply to all four at once" behavior. Wrap Shape:
   *  this SAME control is the single active offset editor (see `isOffsetDisabled`), so only
   *  `wrapOffsetTop` is actually persisted — writing Right/Bottom/Left too would corrupt Bounding
   *  Box's own four independent values if the user later switches back to it. Jump: persists
   *  `wrapOffsetTop` and `wrapOffsetBottom` only, its only two real fields (AC 3.2). The DISPLAYED
   *  value in all four (disabled) boxes always updates regardless of style, though: Wrap Shape's
   *  Right/Bottom/Left fields are mirrors of the one real value, not independent data (see
   *  `offsetStringsFromObj`), so they must stay visually synchronized with whatever was just
   *  committed. `raw` is already validated by the caller (text input) or inherently in-range
   *  (preset/slider); the clamp here is defense-in-depth, not the primary validation.
   *
   *  For multiple selected targets (AC 7/8): each target's OWN current style decides which of its
   *  fields this actually writes (`fieldsForStyle`) — a Wrap Shape target in the selection only
   *  gets `wrapOffsetTop` written even while a Bounding Box target gets all four, in the same
   *  batched commit. */
  const commitAllOffsets = (raw: number) => {
    if (targets.length === 0) return
    const next = Math.max(OFFSET_MIN, Math.min(OFFSET_MAX, raw))
    const entries = buildAllOffsetsEntries(targets, next)

    commonInput.showCommitted(next)
    sideInputs.showAll(next)
    commitToTargets(entries)
  }

  /** Invoked from the shared input's `onBlur` — same revert-or-commit contract as `commitOffset`. */
  const tryCommitCommonInput = (raw: string) => {
    if (targets.length === 0) return

    // A blank field reflects genuinely mixed values across the selection (the "Mixed"
    // placeholder), not an in-progress edit — leave it blank on blur instead of reverting to a
    // stale single number.
    if (raw === '' && commonInput.commonValuesMixed) {
      commonInput.clearError()
      return
    }

    const n = parseOffsetCandidate(raw)

    if (commonInput.commonInputErrorMessage !== '' || raw === '' || n === null) {
      commonInput.revert()
      return
    }

    commitAllOffsets(n)
  }

  return { commitOffset, commitAllOffsets, tryCommitCommonInput }
}
